import { usePromotions } from "../context/PromotionContext";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PiCaretLeft, PiPlus, PiScales, PiTrash, PiX } from "react-icons/pi";
import axiosInstance from "../api/axiosInstance";
import { useComparison } from "../context/ComparisonContext";
import {
  buildComparisonRows,
  formatComparisonValue,
} from "../utils/comparisonProducts";
import { getProductImage } from "../utils/productImages";
import { buildProductDetailPath } from "../utils/productLinks";
import { getProductDisplayName } from "../utils/productDisplayName";
import ProductRatingSummary from "../components/ProductRatingSummary";
import styles from "./Comparison.module.css";

const formatPrice = (value) =>
  `${new Intl.NumberFormat("uk-UA", {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)} ₴`;

const COMPARISON_CARD_WIDTH = 288;

const Comparison = ({ admin = false }) => {
  const {
    comparisonIds,
    removeFromComparison,
    clearComparison,
    removeUnavailableItems,
  } = useComparison();
  const { revision: promotionRevision } = usePromotions();
  const [products, setProducts] = useState([]);
  const [differencesOnly, setDifferencesOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const comparisonKey = comparisonIds.join(",");
  useEffect(() => {
    if (!comparisonKey) {
      setProducts([]);
      setLoadError("");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError("");
    axiosInstance
      .get(admin ? "/products/manage/comparison" : "/products/comparison", {
        params: { ids: comparisonKey },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const nextProducts = Array.isArray(data) ? data : [];
        setProducts(nextProducts);
        removeUnavailableItems(nextProducts.map((product) => product._id));
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError("Не вдалося оновити дані товарів. Спробуйте ще раз.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [admin, comparisonKey, removeUnavailableItems, promotionRevision]);

  const rows = useMemo(
    () => buildComparisonRows(products, differencesOnly),
    [differencesOnly, products],
  );

  if (!comparisonIds.length && !loading) {
    return (
      <section className={styles.emptyState}>
        <PiScales aria-hidden="true" />
        <h1>Порівняння товарів</h1>
        <p>Додайте товари з каталогу, щоб зіставити їхні характеристики.</p>
        <Link to="/products">Перейти до каталогу</Link>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <Link to="/products" className={styles.backLink}>
        <PiCaretLeft aria-hidden="true" />
        До каталогу
      </Link>

      <div className={styles.titleRow}>
        <h1>Порівняння товарів</h1>
        <span className={styles.countBadge}>{products.length}</span>
      </div>
      <div className={styles.toolbar}>
        <label className={styles.differencesToggle}>
          <input
            type="checkbox"
            checked={differencesOnly}
            onChange={(event) => setDifferencesOnly(event.target.checked)}
          />
          <span aria-hidden="true" />
          Лише відмінності
        </label>
        <div className={styles.toolbarActions}>
          <Link to="/products" className={styles.addButton}>
            <PiPlus aria-hidden="true" />
            Додати товар
          </Link>
          <button type="button" className={styles.clearButton} onClick={clearComparison}>
            <PiTrash aria-hidden="true" />
            Очистити список
          </button>
        </div>
      </div>

      {loadError && <p className={styles.error}>{loadError}</p>}
      {loading && <p className={styles.loading}>Оновлюємо дані товарів…</p>}

      {!loading && products.length > 0 && (
        <div
          className={`${styles.tableViewport} ${
            products.length <= 2 ? styles.noMobileScroll : ""
            } ${products.length === 2 ? styles.twoProductMobile : ""}`}
        >
          <table
            className={styles.table}
            data-product-count={products.length}
            style={{
                width: `${products.length * COMPARISON_CARD_WIDTH}px`,
            }}
          >
            <thead>
              <tr>
                {products.map((product) => {
                  const hasDiscount =
                    Number(product.discountPrice) > 0 &&
                    Number(product.discountPrice) < Number(product.price);
                  return (
                    <th key={product._id} className={styles.productColumn}>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removeFromComparison(product._id)}
                        aria-label={`Видалити ${getProductDisplayName(product)} з порівняння`}
                        title="Видалити з порівняння"
                      >
                        <PiX aria-hidden="true" />
                      </button>
                      <div className={styles.productCard}>
                        <Link to={buildProductDetailPath(product)} className={styles.productLink}>
                          <img
                            src={getProductImage(product, "thumbnail")}
                            alt={getProductDisplayName(product)}
                            loading="lazy"
                          />
                          <strong>{getProductDisplayName(product)}</strong>
                        </Link>
                        <div className={styles.productMeta}>
                          <span>Артикул: {product.productCode ?? "Не вказано"}</span>
                          <span>EAN: {product.europeanArticleNumber || "Не вказано"}</span>
                        </div>
                        <div className={styles.ratingSlot}>
                          <ProductRatingSummary product={product} compact />
                        </div>
                        <div className={styles.price}>
                          <b className={hasDiscount ? styles.discountPrice : ""}>
                            {formatPrice(hasDiscount ? product.discountPrice : product.price)}
                          </b>
                          {hasDiscount && <del>{formatPrice(product.price)}</del>}
                        </div>
                        <span className={Number(product.totalAvailable) > 0 ? styles.available : styles.unavailable}>
                          {Number(product.totalAvailable) > 0 ? "В наявності" : "Немає в наявності"}
                        </span>
                        {admin && product.isVisible === false && (
                          <span className={styles.hiddenProduct}>Прихований на сайті</span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.key}>
                  <tr className={styles.attributeRow}>
                    <th colSpan={products.length}>
                      <span className={styles.attributeLabel}>{row.label}</span>
                    </th>
                  </tr>
                  <tr className={styles.valueRow}>
                    {products.map((product) => {
                      const text = formatComparisonValue(row.read(product));
                      return (
                        <td key={product._id}>
                          {row.long && text.length > 90 ? (
                            <details>
                              <summary>{text.slice(0, 80)}…</summary>
                              <p>{text}</p>
                            </details>
                          ) : (
                            text
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              ))}
              {differencesOnly && rows.length === 0 && (
                <tr>
                  <td colSpan={products.length} className={styles.noDifferences}>
                    Серед наявних структурованих характеристик відмінностей немає.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default Comparison;
